import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { Car, MapPin, Clock, Shield, Smartphone, Star, ArrowLeft, Phone, CheckCircle2, Calendar, CreditCard, Users } from 'lucide-react';
import { toast } from 'sonner';

const ValetDemo = () => {
  const { t } = useTranslation();
  const [selectedDate, setSelectedDate] = useState('');
  const [selectedTime, setSelectedTime] = useState('');
  
  const handleBooking = (e) => {
    e.preventDefault();
    toast.success(`✅ ${t('toast.bookingSuccess')}`, {
      description: t('toast.bookingSuccessDesc')
    });
  };
  
  const handleContactClick = () => {
    toast.info(`📞 ${t('toast.contactInfo')}`, {
      description: 'Tel: +49 123 456789 | E-Mail: info@valetpro.de'
    });
  };

  const testimonials = [
    { name: 'Michael K.', text: 'Perfekter Service bei unserer Hochzeit. Alle Gäste waren begeistert!', rating: 5 },
    { name: 'Sandra M.', text: 'Professionell, pünktlich und sehr freundlich. Absolut empfehlenswert.', rating: 5 },
  ];

  const pricingPlans = [
    { name: 'Event Basic', price: '299', features: ['Bis zu 50 Fahrzeuge', '4 Stunden Service', '2 Valet-Mitarbeiter'] },
    { name: 'Event Premium', price: '599', features: ['Bis zu 150 Fahrzeuge', '8 Stunden Service', '4 Valet-Mitarbeiter', 'VIP-Bereich'], popular: true },
    { name: 'Event Deluxe', price: '999', features: ['Unbegrenzt Fahrzeuge', '12 Stunden Service', '8 Valet-Mitarbeiter', 'VIP-Bereich', 'Concierge'] },
  ];

  return (
    <div className="min-h-screen bg-slate-900">
      {/* Back Link */}
      <div className="fixed top-4 left-4 z-50">
        <Link to="/portfolio">
          <Button variant="outline" className="bg-white/10 border-white/20 text-white hover:bg-white/20 backdrop-blur-sm">
            <ArrowLeft size={16} className="mr-2" />
            {t('common.backToPortfolio')}
          </Button>
        </Link>
      </div>

      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-40 bg-slate-900/80 backdrop-blur-md border-b border-white/10">
        <div className="max-w-7xl mx-auto px-4 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-amber-500 flex items-center justify-center">
              <Car className="text-white" size={20} />
            </div>
            <span className="text-white font-semibold text-lg">Valet Parking Pro</span>
          </div>
          <div className="hidden md:flex items-center gap-6 text-sm text-slate-300">
            <a href="#services" className="hover:text-white">{t('common.services')}</a>
            <a href="#pricing" className="hover:text-white">{t('common.pricing')}</a>
            <a href="#booking" className="hover:text-white">{t('common.booking')}</a>
          </div>
          <Button onClick={handleContactClick} className="bg-amber-500 hover:bg-amber-600 text-white">
            <Phone size={16} className="mr-2" />
            {t('buttons.contact')}
          </Button>
        </div>
      </nav>

      {/* Hero */}
      <section className="relative min-h-screen flex items-center bg-gradient-to-br from-slate-900 via-amber-900/20 to-slate-900 pt-16">
        <div className="absolute inset-0 bg-[url('https://images.unsplash.com/photo-1558618666-fcd25c85cd64?w=1920&q=80')] bg-cover bg-center opacity-20" />
        <div className="absolute inset-0 bg-gradient-to-r from-slate-900 via-slate-900/80 to-transparent" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-32">
          <div className="max-w-2xl">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-amber-500/20 border border-amber-500/30 mb-6">
              <Star className="text-amber-400 fill-amber-400" size={16} />
              <span className="text-amber-300 text-sm font-medium">Premium Valet Service seit 2015</span>
            </div>
            <h1 className="text-5xl sm:text-6xl font-bold text-white mb-6 leading-tight">
              Exklusiver Valet Service für Ihre Veranstaltung
            </h1>
            <p className="text-xl text-slate-300 mb-8 leading-relaxed">
              Digitale Buchungsplattform mit Echtzeit-Fahrzeugverfolgung. Professioneller Parkservice für Events, Hochzeiten und Firmenveranstaltungen.
            </p>
            <div className="flex flex-wrap gap-4">
              <Button size="lg" onClick={() => document.getElementById('booking').scrollIntoView({ behavior: 'smooth' })} className="bg-amber-500 hover:bg-amber-600 text-white px-8">
                <Calendar size={18} className="mr-2" />
                {t('buttons.bookNow')}
              </Button>
              <Button size="lg" onClick={() => document.getElementById('services').scrollIntoView({ behavior: 'smooth' })} variant="outline" className="border-white/20 text-white hover:bg-white/10">
                {t('buttons.learnMore')}
              </Button>
            </div>
            
            {/* Stats */}
            <div className="mt-12 grid grid-cols-3 gap-8">
              {[
                { value: '500+', label: 'Events' },
                { value: '50.000+', label: 'Fahrzeuge' },
                { value: '4.9/5', label: 'Bewertung' },
              ].map((stat, i) => (
                <div key={i}>
                  <div className="text-3xl font-bold text-amber-400">{stat.value}</div>
                  <div className="text-slate-400 text-sm">{stat.label}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Services */}
      <section id="services" className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <span className="text-amber-600 font-semibold text-sm uppercase tracking-wider">Unsere Services</span>
            <h2 className="text-4xl font-bold text-slate-900 mt-3">Rundum-Sorglos-Paket</h2>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              { icon: Car, title: 'Valet Parking', desc: 'Professioneller Parkservice direkt vor Ort mit geschultem Personal' },
              { icon: MapPin, title: 'GPS Tracking', desc: 'Echtzeit-Verfolgung Ihres Fahrzeugs über unsere App' },
              { icon: Shield, title: 'Vollkasko', desc: 'Rundum-Schutz für alle Fahrzeuge während des Services' },
              { icon: Clock, title: '24/7 Support', desc: 'Jederzeit erreichbar für Ihre Anfragen' },
            ].map((feature, i) => (
              <Card key={i} className="border-0 shadow-lg text-center group hover:shadow-xl transition-all duration-300 hover:-translate-y-1">
                <CardContent className="p-8">
                  <div className="w-16 h-16 rounded-2xl bg-amber-100 flex items-center justify-center mx-auto mb-6 group-hover:bg-amber-500 transition-colors">
                    <feature.icon className="text-amber-600 group-hover:text-white transition-colors" size={32} />
                  </div>
                  <h3 className="font-bold text-slate-900 mb-3 text-lg">{feature.title}</h3>
                  <p className="text-slate-600">{feature.desc}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing */}
      <section id="pricing" className="py-24 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <span className="text-amber-600 font-semibold text-sm uppercase tracking-wider">Preise</span>
            <h2 className="text-4xl font-bold text-slate-900 mt-3">Transparente Preisgestaltung</h2>
          </div>
          <div className="grid md:grid-cols-3 gap-8">
            {pricingPlans.map((plan, i) => (
              <Card key={i} className={`border-2 ${plan.popular ? 'border-amber-500 shadow-xl scale-105' : 'border-slate-200'} relative`}>
                {plan.popular && (
                  <div className="absolute -top-4 left-1/2 -translate-x-1/2 bg-amber-500 text-white px-4 py-1 rounded-full text-sm font-medium">
                    Beliebt
                  </div>
                )}
                <CardContent className="p-8">
                  <h3 className="text-xl font-bold text-slate-900 mb-2">{plan.name}</h3>
                  <div className="mb-6">
                    <span className="text-4xl font-bold text-slate-900">{plan.price}€</span>
                    <span className="text-slate-500">/Event</span>
                  </div>
                  <ul className="space-y-3 mb-8">
                    {plan.features.map((feature, j) => (
                      <li key={j} className="flex items-center gap-3">
                        <CheckCircle2 className="text-green-500" size={18} />
                        <span className="text-slate-600">{feature}</span>
                      </li>
                    ))}
                  </ul>
                  <Button className={`w-full ${plan.popular ? 'bg-amber-500 hover:bg-amber-600' : 'bg-slate-900 hover:bg-slate-800'} text-white`}>
                    Auswählen
                  </Button>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-24 bg-slate-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <span className="text-amber-400 font-semibold text-sm uppercase tracking-wider">Kundenstimmen</span>
            <h2 className="text-4xl font-bold text-white mt-3">Was unsere Kunden sagen</h2>
          </div>
          <div className="grid md:grid-cols-2 gap-8">
            {testimonials.map((t, i) => (
              <Card key={i} className="bg-slate-800 border-0">
                <CardContent className="p-8">
                  <div className="flex gap-1 mb-4">
                    {[...Array(t.rating)].map((_, j) => (
                      <Star key={j} className="text-amber-400 fill-amber-400" size={18} />
                    ))}
                  </div>
                  <p className="text-slate-300 text-lg mb-6">"{t.text}"</p>
                  <div className="font-semibold text-white">{t.name}</div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Booking Form */}
      <section id="booking" className="py-24 bg-white">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <span className="text-amber-600 font-semibold text-sm uppercase tracking-wider">Anfrage</span>
            <h2 className="text-4xl font-bold text-slate-900 mt-3">Jetzt unverbindlich anfragen</h2>
          </div>
          <Card className="border-0 shadow-xl">
            <CardContent className="p-8">
              <form onSubmit={handleBooking} className="space-y-6">
                <div className="grid sm:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">Name</label>
                    <Input placeholder="Ihr Name" className="bg-slate-50" required />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">E-Mail</label>
                    <Input type="email" placeholder="ihre@email.de" className="bg-slate-50" required />
                  </div>
                </div>
                <div className="grid sm:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">Event-Datum</label>
                    <Input type="date" className="bg-slate-50" required />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">Anzahl Fahrzeuge (ca.)</label>
                    <Input type="number" placeholder="100" className="bg-slate-50" required />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">Nachricht</label>
                  <textarea className="w-full h-32 px-4 py-3 bg-slate-50 border border-slate-200 rounded-lg resize-none" placeholder="Beschreiben Sie Ihr Event..."></textarea>
                </div>
                <Button type="submit" className="w-full bg-amber-500 hover:bg-amber-600 text-white py-6 text-lg">
                  Anfrage senden
                </Button>
              </form>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 bg-slate-900 border-t border-slate-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="flex items-center justify-center gap-3 mb-6">
            <div className="w-10 h-10 rounded-lg bg-amber-500 flex items-center justify-center">
              <Car className="text-white" size={20} />
            </div>
            <span className="text-white font-semibold text-lg">Valet Parking Pro</span>
          </div>
          <p className="text-slate-400 mb-4">Premium Valet Service für exklusive Veranstaltungen</p>
          <p className="text-slate-500 text-sm">© 2024 Valet Parking Pro. Alle Rechte vorbehalten.</p>
        </div>
      </footer>

      {/* Demo Badge */}
      <div className="fixed bottom-4 right-4 bg-blue-600 text-white px-4 py-2 rounded-full text-sm font-medium shadow-lg z-50">
        {t('common.demoProject')}
      </div>
    </div>
  );
};

export default ValetDemo;
